import { useContext, useMemo } from 'react';

import { Locale, UserContext } from '~/auth/userContext';

export interface LocaleTerms {
  // Vehicle terminology
  vehicle: string;
  vehicles: string;
  vehiclesServiced: string;

  // Unit/Elevator terminology
  unit: string;
  units: string;
  elevators: string;
  elevatorType: string;

  // Compliance terminology
  compliance: string;
  compliances: string;

  // Violation/Defect terminology
  violation: string;
  violations: string;

  // Compliance status label shown when a unit is past its due/expiration date
  inspectionStatusExpired: string;

  // Callback terminology
  callback: string;
  callbacks: string;
  callbackRate: string;
  regularCallback: string;
  regularCallbacks: string;
  deferredCallbacks: string;
  regularCallbackResponse: string;
  emergencyCallbackResponse: string;
  callbackRateTarget: string;
  emergencyCallbackRateTarget: string;
  emergencyCallbackRate: string;
  annualizedCallbackRate: string;
}

const localeTermsMap: Record<Locale, LocaleTerms> = {
  'en-US': {
    vehicle: 'Car',
    vehicles: 'Cars',
    vehiclesServiced: 'Cars Serviced',
    unit: 'Unit',
    units: 'Units',
    elevators: 'Elevators',
    elevatorType: 'Elevator Type',
    compliance: 'Compliance',
    compliances: 'Compliances',
    violation: 'Violation',
    violations: 'Violations',
    inspectionStatusExpired: 'Expired',
    callback: 'Callback',
    callbacks: 'Callbacks',
    callbackRate: 'Callback Rate',
    regularCallback: 'Regular Callback',
    regularCallbacks: 'Regular Callbacks',
    deferredCallbacks: 'Deferred Callbacks',
    regularCallbackResponse: 'Regular Callback Response',
    emergencyCallbackResponse: 'Emergency Callback Response',
    callbackRateTarget: 'Callback Rate Target',
    emergencyCallbackRateTarget: 'Emergency Callback Rate Target',
    emergencyCallbackRate: 'Emergency Callback Rate',
    annualizedCallbackRate: 'Annualized Callback Rate',
  },
  'en-GB': {
    vehicle: 'Lift',
    vehicles: 'Lifts',
    vehiclesServiced: 'Lifts Serviced',
    unit: 'Lift',
    units: 'Lifts',
    elevators: 'Lifts',
    elevatorType: 'Lift Type',
    compliance: 'LOLERS',
    compliances: 'LOLERS',
    violation: 'Defect',
    violations: 'Defects',
    inspectionStatusExpired: 'Overdue',
    callback: 'Callout',
    callbacks: 'Callouts',
    callbackRate: 'Callout Rate',
    regularCallback: 'Callout',
    regularCallbacks: 'Callouts',
    deferredCallbacks: 'Deferred Callouts',
    regularCallbackResponse: 'Callout Response',
    emergencyCallbackResponse: 'Emergency Callout Response',
    callbackRateTarget: 'Callout Rate Target',
    emergencyCallbackRateTarget: 'Emergency Callout Rate Target',
    emergencyCallbackRate: 'Emergency Callout Rate',
    annualizedCallbackRate: 'Annualised Callout Rate',
  },
};

export const getLocaleTerms = (locale: Locale): LocaleTerms => {
  return localeTermsMap[locale];
};

export const useLocaleTerms = (): LocaleTerms => {
  const { locale } = useContext(UserContext);

  return useMemo(() => getLocaleTerms(locale), [locale]);
};
